import { formatMoney } from './format';
import { money, type MoneyInput } from './money';

/**
 * Pure pre-trade risk evaluation. The backend gathers the context from the
 * database/Deriv and calls this before EVERY order (manual or automated).
 * If any input cannot be verified the result is a rejection — never a pass.
 */

export type RiskAction = 'SKIP' | 'REJECT' | 'PAUSE_SESSION' | 'END_SESSION';

export type RiskCode =
  | 'CONNECTION_UNVERIFIED'
  | 'BALANCE_UNVERIFIED'
  | 'INSUFFICIENT_BALANCE'
  | 'REAL_TRADING_DISABLED'
  | 'TRADING_BLOCKED'
  | 'INVALID_STAKE'
  | 'STAKE_LIMIT'
  | 'BROKER_STAKE_LIMIT'
  | 'DAILY_LOSS_LIMIT'
  | 'DAILY_TRADES_LIMIT'
  | 'USER_CONCURRENCY_LIMIT'
  | 'EXPOSURE_LIMIT'
  | 'SESSION_ERRORS'
  | 'SESSION_MAX_TRADES'
  | 'SESSION_TARGET_REACHED'
  | 'SESSION_MAX_LOSS'
  | 'SESSION_CONCURRENCY'
  | 'UNRESOLVED_ORDER'
  | 'RISK_CONTEXT_UNVERIFIED';

export type RiskResult =
  | { ok: true }
  | { ok: false; code: RiskCode; action: RiskAction; message: string };

export interface UserRiskLimits {
  maxStake: MoneyInput;
  maxExposure: MoneyInput;
  maxDailyLoss: MoneyInput;
  maxDailyTrades: number;
  maxConcurrent: number;
  maxConsecutiveErrors: number;
  blockedUntil?: Date | null;
}

export interface RiskContext {
  now: Date;
  stake: MoneyInput;
  account: {
    type: 'DEMO' | 'REAL';
    /** null when unknown */
    balance: MoneyInput | null;
    balanceUpdatedAt: Date | null;
    realTradingAllowed: boolean;
    connectionVerified: boolean;
  };
  limits: UserRiskLimits;
  /** Aggregates for the user's account for the current UTC day. */
  daily: { netProfit: MoneyInput; trades: number; openExposure: MoneyInput; openContracts: number; pendingExposure?: MoneyInput; unknownOrders?: number };
  /** The broker's limits of one entry, in `currency` (the account's; USD when not given). */
  broker?: { minStake?: MoneyInput | null; maxStake?: MoneyInput | null; currency?: string };
  session?: {
    netProfit: MoneyInput;
    trades: number;
    openContracts: number;
    openExposure: MoneyInput;
    maxLoss: MoneyInput;
    targetProfit: MoneyInput;
    maxTrades: number;
    maxConcurrent: number;
    consecutiveErrors: number;
  };
}

export const BALANCE_MAX_AGE_MS = 120_000;

function fail(code: RiskCode, action: RiskAction, message: string): RiskResult {
  return { ok: false, code, action, message };
}

function verifiedMoney(value: MoneyInput, minimum?: number): boolean {
  if (value === null || value === undefined || (typeof value === 'string' && !value.trim())) return false;
  try { const amount = money(value); return amount.isFinite() && (minimum === undefined || amount.gte(minimum)); }
  catch { return false; }
}

const count = (value: number, min = 0) => Number.isSafeInteger(value) && value >= min;

export function evaluateRisk(ctx: RiskContext): RiskResult {
  const s = ctx.session;
  const sessionFail = (code: RiskCode, sessionAction: RiskAction, message: string) =>
    fail(code, s ? sessionAction : 'REJECT', message);

  if (!verifiedMoney(ctx.stake, 0) || money(ctx.stake).lte(0)) return fail('INVALID_STAKE', 'REJECT', 'Valor de entrada inválido.');
  const stake = money(ctx.stake);
  const limits = ctx.limits;
  if (!Number.isFinite(ctx.now.getTime()) ||
      ![limits.maxStake, limits.maxExposure, limits.maxDailyLoss].every((v) => verifiedMoney(v, 0) && money(v).gt(0)) ||
      ![limits.maxDailyTrades, limits.maxConcurrent, limits.maxConsecutiveErrors].every((v) => count(v, 1)) ||
      !verifiedMoney(ctx.daily.netProfit) || !verifiedMoney(ctx.daily.openExposure, 0) || !verifiedMoney(ctx.daily.pendingExposure ?? 0, 0) ||
      ![ctx.daily.trades, ctx.daily.openContracts, ctx.daily.unknownOrders ?? 0].every((v) => count(v)) ||
      (limits.blockedUntil && !Number.isFinite(limits.blockedUntil.getTime())) ||
      (s && (!verifiedMoney(s.netProfit) || !verifiedMoney(s.openExposure, 0) || !verifiedMoney(s.maxLoss, 0) || !verifiedMoney(s.targetProfit, 0) ||
        ![s.maxTrades, s.maxConcurrent].every((v) => count(v, 1)) || ![s.trades, s.openContracts, s.consecutiveErrors].every((v) => count(v)))) ||
      (ctx.broker?.minStake != null && !verifiedMoney(ctx.broker.minStake, 0)) ||
      (ctx.broker?.maxStake != null && !verifiedMoney(ctx.broker.maxStake, 0))) {
    return sessionFail('RISK_CONTEXT_UNVERIFIED', 'PAUSE_SESSION', 'Não foi possível verificar os limites e a exposição da conta.');
  }

  if (!ctx.account.connectionVerified) {
    return sessionFail('CONNECTION_UNVERIFIED', 'PAUSE_SESSION', 'Conexão com a corretora não verificada.');
  }
  if (ctx.limits.blockedUntil && ctx.limits.blockedUntil.getTime() > ctx.now.getTime()) {
    return sessionFail('TRADING_BLOCKED', 'PAUSE_SESSION', 'Operações bloqueadas temporariamente.');
  }
  if (ctx.account.type === 'REAL' && !ctx.account.realTradingAllowed) {
    return sessionFail('REAL_TRADING_DISABLED', 'END_SESSION', 'Operações indisponíveis no momento.');
  }
  if ((ctx.daily.unknownOrders ?? 0) > 0) {
    return fail('UNRESOLVED_ORDER', s ? 'SKIP' : 'REJECT', 'Aguardando a reconciliação de uma compra sem confirmação.');
  }

  // Session stop conditions come first: they end the session cleanly.
  if (s) {
    if (s.consecutiveErrors >= ctx.limits.maxConsecutiveErrors) {
      return fail('SESSION_ERRORS', 'PAUSE_SESSION', 'Sessão pausada após erros consecutivos.');
    }
    if (money(s.netProfit).gte(money(s.targetProfit)) && money(s.targetProfit).gt(0)) {
      return fail('SESSION_TARGET_REACHED', 'END_SESSION', 'Meta da sessão atingida.');
    }
    if (s.trades >= s.maxTrades) {
      return fail('SESSION_MAX_TRADES', 'END_SESSION', 'Número máximo de operações da sessão atingido.');
    }
    const worst = money(s.netProfit).minus(money(s.openExposure)).minus(stake);
    if (worst.lt(money(s.maxLoss).negated())) {
      return fail('SESSION_MAX_LOSS', 'END_SESSION', 'A próxima entrada poderia ultrapassar a perda máxima da sessão.');
    }
    if (s.openContracts >= s.maxConcurrent) {
      return fail('SESSION_CONCURRENCY', 'SKIP', 'Aguardando contratos abertos da sessão.');
    }
  }

  // Account-level limits apply to manual orders. An automation is bounded by its own
  // configuration (meta, limite de perda, gales), which the user set and confirmed.
  if (!s && stake.gt(money(ctx.limits.maxStake))) {
    return fail('STAKE_LIMIT', 'REJECT', 'Entrada acima do seu limite por operação.');
  }
  if (ctx.broker?.minStake != null && stake.lt(money(ctx.broker.minStake))) {
    return sessionFail('BROKER_STAKE_LIMIT', 'END_SESSION', `Entrada mínima da corretora: ${formatMoney(ctx.broker.minStake, ctx.broker.currency ?? 'USD')}.`);
  }
  if (ctx.broker?.maxStake != null && stake.gt(money(ctx.broker.maxStake))) {
    return sessionFail('BROKER_STAKE_LIMIT', 'END_SESSION', `Entrada máxima da corretora: ${formatMoney(ctx.broker.maxStake, ctx.broker.currency ?? 'USD')}.`);
  }

  if (ctx.account.balance === null || !verifiedMoney(ctx.account.balance, 0) || ctx.account.balanceUpdatedAt === null || !Number.isFinite(ctx.account.balanceUpdatedAt.getTime())) {
    return sessionFail('BALANCE_UNVERIFIED', 'PAUSE_SESSION', 'Saldo não verificado.');
  }
  if (ctx.now.getTime() - ctx.account.balanceUpdatedAt.getTime() > BALANCE_MAX_AGE_MS || ctx.account.balanceUpdatedAt.getTime() > ctx.now.getTime() + 5_000) {
    return sessionFail('BALANCE_UNVERIFIED', 'PAUSE_SESSION', 'Saldo desatualizado.');
  }
  if (money(ctx.account.balance).minus(money(ctx.daily.pendingExposure ?? 0)).lt(stake)) {
    return sessionFail('INSUFFICIENT_BALANCE', 'END_SESSION', 'Saldo insuficiente.');
  }

  if (s) return { ok: true };

  const dailyWorst = money(ctx.daily.netProfit).minus(money(ctx.daily.openExposure)).minus(stake);
  if (dailyWorst.lt(money(ctx.limits.maxDailyLoss).negated())) {
    return fail('DAILY_LOSS_LIMIT', 'REJECT', 'Limite de perda diária atingido.');
  }
  if (ctx.daily.trades >= ctx.limits.maxDailyTrades) {
    return fail('DAILY_TRADES_LIMIT', 'REJECT', 'Limite diário de operações atingido.');
  }
  if (ctx.daily.openContracts >= ctx.limits.maxConcurrent) {
    return fail('USER_CONCURRENCY_LIMIT', 'REJECT', 'Limite de contratos simultâneos atingido.');
  }
  if (money(ctx.daily.openExposure).plus(stake).gt(money(ctx.limits.maxExposure))) {
    return fail('EXPOSURE_LIMIT', 'REJECT', 'Exposição máxima em aberto atingida.');
  }
  return { ok: true };
}
